import React, {useState} from "react";
import CustomModal from "../modal/customModal";
import Button from "../button/button";
import {ButtonStatus} from "../button/enum";
import Calendar from "react-calendar";
import "./customCalendar.css";
import moment from "moment";
import {formatDate} from "../../util/util";

interface IProps {
    isOpen: boolean,
    dataRange?: DateRange,
    onCancel: () => void,
    onApply: (dataRange: DateRange) => void,
}

export enum DateRangeItemKey {
    today,
    yesterday,
    thisWeek,
    lastWeek,
    last7Days,
    last28Days,
    last30Days,
    last90Days,
    last12Months,
    lastCalendarYear,
    thisYear,
    custom,
}

export interface DateRange {
    id: DateRangeItemKey,
    title: string,
    from: Date,
    to: Date
}

export function getDefaultDateRange(): DateRange {
    const dateRangeItem = DateRangeSource.find((dr) => dr.id === DateRangeItemKey.last30Days) as DateRangeItem
    const [fromDate, toDate] = calculateDatesFromDateRangeItem(dateRangeItem)
    return {
        ...dateRangeItem,
        from: fromDate || new Date(),
        to: toDate || new Date(),
    }
}

interface DateRangeItem {
   id: DateRangeItemKey,
   title: string,
}

const DateRangeSource = [
    {id: DateRangeItemKey.today, title: "Today"},
    {id: DateRangeItemKey.yesterday, title: "Yesterday"},
    {id: DateRangeItemKey.thisWeek, title: "This week (Sun - Today)"},
    {id: DateRangeItemKey.lastWeek, title: "Last week (Sun - Sat)"},
    {id: DateRangeItemKey.last7Days, title: "Last 7 days"},
    {id: DateRangeItemKey.last28Days, title: "Last 28 days"},
    {id: DateRangeItemKey.last30Days, title: "Last 30 days"},
    {id: DateRangeItemKey.last90Days, title: "Last 90 days"},
    {id: DateRangeItemKey.last12Months, title: "Last 12 months"},
    {id: DateRangeItemKey.lastCalendarYear, title: "Last calendar year"},
    {id: DateRangeItemKey.thisYear, title: "This year (Jan - Today)"},
    {id: DateRangeItemKey.custom, title: "Custom"},
]

const calculateDatesFromDateRangeItem = (dataRangeItem: DateRangeItem): [Date?, Date?] => {
    if (dataRangeItem.id === DateRangeItemKey.today) {
        const today = new Date()
        return [today, today]
    } else if (dataRangeItem.id === DateRangeItemKey.yesterday) {
        const yesterday = moment().subtract(1, "day")
            .toDate()
        return [yesterday, yesterday]
    } else if (dataRangeItem.id === DateRangeItemKey.thisWeek) {
        const m = moment()
        const weekday = m.weekday()
        const fromDate = m.subtract(weekday, "day")
            .toDate()
        return [fromDate, new Date()]
    } else if (dataRangeItem.id === DateRangeItemKey.lastWeek) {
        const m = moment()
        const weekday = m.weekday()
        const fromDate = moment().subtract(weekday + 7, "day")
            .toDate()
        const toDate = m.subtract(weekday + 1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.last7Days) {
        const fromDate = moment().subtract(7, "day")
            .toDate()
        const toDate = moment().subtract(1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.last28Days) {
        const fromDate = moment().subtract(28, "day")
            .toDate()
        const toDate = moment().subtract(1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.last30Days) {
        const fromDate = moment().subtract(30, "day")
            .toDate()
        const toDate = moment().subtract(1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.last90Days) {
        const fromDate = moment().subtract(90, "day")
            .toDate()
        const toDate = moment().subtract(1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.last12Months) {
        const fromDate = moment().subtract(365, "day")
            .toDate()
        const toDate = moment().subtract(1, "day")
            .toDate()
        return [fromDate, toDate]
    } else if (dataRangeItem.id === DateRangeItemKey.lastCalendarYear) {
        const year = (new Date()).getFullYear()
        return [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)]
    } else if (dataRangeItem.id === DateRangeItemKey.thisYear) {
        const year = (new Date()).getFullYear()
        return [new Date(year, 0, 1), new Date()]
    }
    return [undefined, undefined]
}

enum EditingDate {
    start,
    end,
}

export default function DateRangePicker(props: IProps) {
    const {isOpen, dataRange = getDefaultDateRange(), onCancel, onApply} = props
    const defaultDateRangeItem = DateRangeSource.find((s) => s.id === dataRange.id) as DateRangeItem
    const [selectedDateRangeItem, setSelectedDateRangeItem] = useState<DateRangeItem>(defaultDateRangeItem)
    const [startDate, setStartDate] = useState<Date>(dataRange.from)
    const [endDate, setEndDate] = useState<Date>(dataRange.to)
    const [editingDate, setEditingDate] = useState<EditingDate>(EditingDate.start)

    const onSelectMenuItem = (dateRangeItem: DateRangeItem) => {
        setSelectedDateRangeItem(dateRangeItem)

        const [fromDate, toDate] = calculateDatesFromDateRangeItem(dateRangeItem)
        fromDate && setStartDate(fromDate)
        toDate && setEndDate(toDate)
    }

    const $checkedIcon = (
        <svg className="icon" viewBox="0 0 1025 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2175" width="16" height="16">
            <path d="M364.032 896c-13.653333 0-27.221333-5.12-35.754667-15.445333L15.36 559.914667c-20.437333-20.565333-18.730667-53.162667 0-73.728 20.394667-20.565333 52.736-18.858667 73.130667 0l275.584 284.629333L937.258667 186.112a51.029333 51.029333 0 0 1 73.130666 0 52.053333 52.053333 0 0 1 0 73.728L399.744 880.554667c-10.24 10.282667-22.144 15.445333-35.712 15.445333z"
                p-id="2176" fill="#2F80ED"/>
        </svg>
    )

    const $menuItem = (dateRangeItem: DateRangeItem) => {
        const {id, title} = dateRangeItem
        const isSelected = selectedDateRangeItem.id === id
        return (
            <div key={id} className={"flex flex-row justify-between items-center px-5 py-2 bg-white hover:bg-gray-6 rounded cursor-pointer"} onClick={() => {
                onSelectMenuItem(dateRangeItem)
            }}>
                <p className={`text-base ${isSelected ? "text-primary" : "text-gray-1"} font-semibold`}>{title}</p>
                {isSelected ? $checkedIcon : null}
            </div>
        )
    }

    const $leftMenu = (
        <div className={"w-64 flex flex-col py-3 border-r border-gray-5"}>
            {DateRangeSource.map((dateRangeItem) => $menuItem(dateRangeItem))}
        </div>
    )

    const onSelectDate = (date: Date) => {
        if (editingDate === EditingDate.start) {
            if (!moment(date).isSame(moment(startDate), "day")) {
                setStartDate(date)
                setSelectedDateRangeItem(DateRangeSource.find((dr) => dr.id === DateRangeItemKey.custom) as DateRangeItem)
            }
        } else if (editingDate === EditingDate.end) {
            if (!moment(date).isSame(moment(endDate), "day")) {
                setEndDate(date)
                setSelectedDateRangeItem(DateRangeSource.find((dr) => dr.id === DateRangeItemKey.custom) as DateRangeItem)
            }
        }
    }

    const getCalendarMaxDate = () => {
        if (editingDate === EditingDate.start) {
            return endDate
        }
        return new Date()
    }

    const getCalendarMinDate = () => {
        if (editingDate === EditingDate.end) {
            return startDate
        }
        return new Date(2020, 0, 1)
    }

    const getCalendarValue = () => {
        if (editingDate === EditingDate.start) {
            return startDate
        } else if (editingDate === EditingDate.end) {
            return endDate
        }
        return new Date()
    }

    const $calendar = (
        <div key={getCalendarValue().toISOString()} className={"flex flex-col pt-5"}>
            <Calendar
                locale={"en"}
                calendarType={"US"}
                minDate={getCalendarMinDate()}
                maxDate={getCalendarMaxDate()}
                value={getCalendarValue()}
                onClickDay={onSelectDate}
            />
        </div>
    )

    const $dateBtn = (dt: EditingDate, title: string) => {
        let dynamicStyle = ""
        if (dt === editingDate) {
            dynamicStyle = "border-primary text-white bg-primary"
        } else {
            dynamicStyle = "border-primary text-primary bg-white"
        }
        return (
            <button className={`px-4 py-1.5 rounded border text-sm font-semibold focus:outline-none ${dynamicStyle}`} onClick={() => {
                dt !== editingDate && setEditingDate(dt)
            }}>
                {title}
            </button>
        )
    }

    const $preview = (
        <div className={"flex flex-col p-5 border-b border-gray-5"}>
            <p className={"text-base text-gray-1 font-semibold"}>{selectedDateRangeItem.title}</p>
            <div className={"flex flex-row items-center mt-2"}>
                {$dateBtn(EditingDate.start, formatDate(startDate))}
                <div className={"w-3 h-px mx-6 bg-gray-2"}/>
                {$dateBtn(EditingDate.end, formatDate(endDate))}
            </div>
        </div>
    )

    const $rightCalendar = (
        <div className={"flex flex-col"}>
            {$preview}
            {$calendar}
        </div>
    )

    const onApplyDataRange = () => {
        const dataRange: DateRange = {
            ...selectedDateRangeItem,
            from: startDate,
            to: endDate,
        }
        onApply && onApply(dataRange)
    }

    const $footer = (
        <div className={"flex flex-row justify-end items-center border-t border-gray-5 p-5 space-x-6"}>
            <Button status={ButtonStatus.light} onClick={onCancel}>Cancel</Button>
            <Button status={ButtonStatus.primary} onClick={onApplyDataRange}>Apply</Button>
        </div>
    )

    return (
        <CustomModal isOpen={isOpen} containerClassName={"px-0 py-0"}>
            <div className={"flex flex-col"}>
                <div className={"flex flex-row"}>
                    {$leftMenu}
                    {$rightCalendar}
                </div>
                {$footer}
            </div>
        </CustomModal>
    )
}
